import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Input, Space, Table, Typography } from 'antd';
import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import { useTandem } from '../tandem/TandemSessionBoundary';

export type VisibilityInstrument = {
  key: string;
  ticker: string;
  name: string;
  asset_ids: number[];
};
export type VisibilityData = {
  session_id: number;
  instruments: VisibilityInstrument[];
  hidden_asset_ids: number[];
  editable: boolean;
};
type Props = {
  sessionId: number;
  engine: 'legacy' | 'fast';
  enabled: boolean;
  readOnly?: boolean;
  hiddenAssetIds?: number[];
  scopeKey?: string;
  onSaved?: (ids: number[]) => void;
};
const signature = (ids: number[]) =>
  [...new Set(ids)].sort((a, b) => a - b).join(',');
const keysFor = (data: VisibilityData) =>
  data.instruments
    .filter(row => row.asset_ids.some(id => data.hidden_asset_ids.includes(id)))
    .map(row => row.key);
const idsFor = (data: VisibilityData, keys: string[]) =>
  data.instruments
    .filter(row => keys.includes(row.key))
    .flatMap(row => row.asset_ids);

/** Shared draft/UI contract; each engine keeps its own authorized API and stream. */
export function useInstrumentVisibility({
  sessionId,
  engine,
  enabled,
  readOnly,
  hiddenAssetIds = [],
  scopeKey = '',
  onSaved,
}: Props) {
  const tandem = useTandem();
  const [data, setData] = useState<VisibilityData | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [reload, setReload] = useState(0);
  const current = useRef({ data, draft });
  current.current = { data, draft };
  const generation = useRef(0);
  const readSequence = useRef(0);
  const discard = useRef(false);
  const incoming = signature(hiddenAssetIds);
  const path =
    engine === 'fast'
      ? `/fast/sessions/${sessionId}/instrument-visibility/`
      : `/sessions/${sessionId}/instrument_visibility/`;
  const request = async (
    payload?: unknown,
    signal?: AbortSignal,
  ): Promise<VisibilityData> => {
    const response = await apiCaller({
      apiUrl: API_URL,
      path,
      method: payload === undefined ? 'GET' : 'POST',
      data: payload || {},
      signal,
    });
    if (response.status < 200 || response.status >= 300) {
      const body = response.result;
      throw new Error(
        typeof body === 'string'
          ? body
          : body?.detail ||
            (Array.isArray(body)
              ? body.join('; ')
              : 'Could not update player visibility. Please retry.'),
      );
    }
    if (
      response.result?.session_id !== sessionId ||
      !Array.isArray(response.result?.instruments)
    )
      throw new Error('Could not load player visibility. Please retry.');
    return response.result;
  };
  useEffect(() => {
    current.current = { data: null, draft: [] };
    setData(null);
    setDraft([]);
    setError('');
    setConflict(false);
    setSaving(false);
    generation.current += 1;
    return () => {
      generation.current += 1;
    };
  }, [sessionId, engine, enabled]);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const sequence = ++readSequence.current;
    setLoading(true);
    void request(undefined, controller.signal)
      .then(next => {
        if (controller.signal.aborted || sequence !== readSequence.current)
          return;
        const previous = current.current;
        const dirty =
          previous.data &&
          signature(idsFor(previous.data, previous.draft)) !==
            signature(previous.data.hidden_asset_ids);
        if (
          !discard.current &&
          dirty &&
          previous.data &&
          signature(next.hidden_asset_ids) !==
            signature(previous.data.hidden_asset_ids)
        ) {
          setConflict(true);
          setError(
            'Player visibility changed elsewhere. Reload visibility before saving.',
          );
        } else {
          setData(next);
          if (!dirty || discard.current) setDraft(keysFor(next));
          setConflict(false);
          setError('');
        }
        discard.current = false;
      })
      .catch(reason => {
        if (!controller.signal.aborted && sequence === readSequence.current)
          setError(String(reason.message || reason));
      })
      .finally(() => {
        if (!controller.signal.aborted && sequence === readSequence.current)
          setLoading(false);
      });
    return () => controller.abort();
    // Refresh only on explicit retry or a session visibility/universe update, never polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, engine, enabled, incoming, scopeKey, reload]);
  const disabled =
    !data?.editable || !!readOnly || !!tandem || loading || saving || conflict;
  const dirty =
    !!data &&
    signature(idsFor(data, draft)) !== signature(data.hidden_asset_ids);
  const change = (keys: string[], hidden: boolean) => {
    if (disabled) return;
    setDraft(old =>
      hidden
        ? [...new Set([...old, ...keys])]
        : old.filter(key => !keys.includes(key)),
    );
  };
  const save = async () => {
    if (!data || disabled || !dirty) return;
    const attempt = generation.current;
    readSequence.current += 1;
    setLoading(false);
    setSaving(true);
    setError('');
    try {
      const next = await request({
        hidden_keys: draft,
        expected_hidden_asset_ids: data.hidden_asset_ids,
      });
      if (attempt !== generation.current) return;
      readSequence.current += 1;
      setLoading(false);
      current.current = { data: next, draft: keysFor(next) };
      setData(next);
      setDraft(keysFor(next));
      setConflict(false);
      setError('');
      onSaved?.(next.hidden_asset_ids);
    } catch (reason) {
      if (attempt === generation.current)
        setError(String((reason as Error).message || reason));
    } finally {
      if (attempt === generation.current) setSaving(false);
    }
  };
  return {
    enabled,
    data,
    draft,
    disabled,
    dirty,
    loading,
    saving,
    error,
    change,
    save,
    retry: () => {
      discard.current = true;
      setReload(n => n + 1);
    },
    forAsset: (id: number) =>
      data?.instruments.find(row => row.asset_ids.includes(id)),
  };
}
export type VisibilityController = ReturnType<typeof useInstrumentVisibility>;

export function VisibilityCheckbox({
  visibility,
  assetId,
}: {
  visibility: VisibilityController;
  assetId: number;
}) {
  const row = visibility.forAsset(assetId);
  if (!visibility.enabled || !row) return null;
  return (
    <Checkbox
      checked={visibility.draft.includes(row.key)}
      disabled={visibility.disabled}
      aria-label={`Hide ${row.ticker} from players`}
      onChange={event => visibility.change([row.key], event.target.checked)}
    >
      Hide from players{row.key.startsWith('family:') ? ` (${row.ticker})` : ''}
    </Checkbox>
  );
}

export function VisibilityToolbar({
  visibility,
  filteredIds,
}: {
  visibility: VisibilityController;
  filteredIds: number[];
}) {
  if (!visibility.enabled) return null;
  const keys =
    visibility.data?.instruments
      .filter(row => row.asset_ids.some(id => filteredIds.includes(id)))
      .map(row => row.key) || [];
  return (
    <div className="instrument-visibility" style={{ margin: '12px 0' }}>
      <Space wrap>
        <Typography.Text strong>Player visibility</Typography.Text>
        <Typography.Text>
          {visibility.draft.length}{' '}
          {visibility.draft.length === 1 ? 'instrument' : 'instruments'} hidden
        </Typography.Text>
        <Button
          size="small"
          disabled={visibility.disabled || !keys.length}
          onClick={() => visibility.change(keys, true)}
        >
          Hide filtered
        </Button>
        <Button
          size="small"
          disabled={visibility.disabled || !keys.length}
          onClick={() => visibility.change(keys, false)}
        >
          Show filtered
        </Button>
        <Button
          size="small"
          disabled={visibility.disabled || !visibility.draft.length}
          onClick={() => visibility.change(visibility.draft, false)}
        >
          Show all
        </Button>
        <Button
          size="small"
          type="primary"
          loading={visibility.saving}
          disabled={visibility.disabled || !visibility.dirty}
          onClick={() => void visibility.save()}
        >
          Save player visibility
        </Button>
        {visibility.dirty && (
          <Typography.Text type="secondary">Unsaved changes</Typography.Text>
        )}
        {visibility.loading && (
          <Typography.Text type="secondary">
            Loading visibility…
          </Typography.Text>
        )}
      </Space>
      {visibility.error && (
        <Alert
          type="error"
          showIcon
          title={visibility.error}
          action={
            <Button size="small" onClick={visibility.retry}>
              Reload visibility
            </Button>
          }
        />
      )}
    </div>
  );
}

/** Used by settings surfaces without a grouped catalogue table. */
export function VisibilityEditor({
  visibility,
}: {
  visibility: VisibilityController;
}) {
  const [query, setQuery] = useState('');
  if (!visibility.enabled) return null;
  const rows = (visibility.data?.instruments || []).filter(row =>
    `${row.ticker} ${row.name}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <div>
      <Input.Search
        aria-label="Search player visibility"
        placeholder="Search instruments"
        value={query}
        onChange={e => setQuery(e.target.value)}
        allowClear
      />
      <VisibilityToolbar
        visibility={visibility}
        filteredIds={rows.flatMap(row => row.asset_ids)}
      />
      <Table<VisibilityInstrument>
        rowKey="key"
        size="small"
        dataSource={rows}
        loading={visibility.loading}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        columns={[
          { title: 'Instrument', dataIndex: 'ticker' },
          { title: 'Name', dataIndex: 'name' },
          {
            title: 'Player visibility',
            render: (_, row) => (
              <VisibilityCheckbox
                visibility={visibility}
                assetId={row.asset_ids[0]}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
